import React, { useState } from "react";
import type { SortedSpecType } from "../../../../../../common-deprecated/types/SpecTypes";
import Icon from "../../../../../../common-deprecated/styles/v2/toyota/globals/Icon";
import Popover from "../../../../../../common-deprecated/components/popover/aem/Popover";
import { type BuildAndBuyDispatchType, useBnbSelector } from "../../../redux/store";
import * as Flex from "../../../../../../common-deprecated/styles/v2/toyota/globals/Flex";
import * as Button from "../../../../../../common-deprecated/styles/v2/toyota/components/Button";
import * as Type from "../../../../../../common-deprecated/styles/v2/toyota/globals/Type";
import * as Styles from "./styles/SpecList";
import * as IconWrapper from "../../../../../../common-deprecated/styles/v2/toyota/globals/IconWrapper";
import * as SummaryStyles from "./styles/Summary";
import { showModal } from "../../../../../../common-deprecated/redux/actions/ModalActions";
import { BnbLabel, getBnbLabel, useBnbOptionalLabel } from "../../../utils/labels";
import { MODAL_CONTENT } from "../../../../../../common-deprecated/utils/modalConstants";
import SpecificationValue from "../common/SpecificationValue";
import SrOnly from "../../../../../../common-deprecated/styles/v2/toyota/utilities/ScreenreaderOnly";
import { useUniqueId } from "../../../../../../common-deprecated/hooks";
import { getEvDisclaimer } from "../../../utils/settings/bnbSettingUtils";
import { useDispatch } from "../../../../../../shared-logic/redux/store";

type SpecListPropsType = {
    specs: SortedSpecType[];
    title?: React.ReactNode;
    singleColumn?: boolean;
    noDropdown?: boolean;
    trackOpen?: () => void;
    defaultOpen?: boolean;
    children?: React.ReactNode;
};

const SpecList = (props: SpecListPropsType): JSX.Element => {
    const uniqueId = useUniqueId();
    const { specs, title, trackOpen, defaultOpen, children, singleColumn, noDropdown } = props;
    const dispatch = useDispatch<BuildAndBuyDispatchType>();
    const commonSettings = useBnbSelector((state) => state.commonSettings);
    const bnbSettings = useBnbSelector((state) => state.bnbSettings);

    const [isOpen, setIsOpen] = useState(!!defaultOpen);
    const showAccessoryDisclaimer = !!useBnbOptionalLabel("accessoryDisclaimer");

    const toggleState = (): void => {
        if (!isOpen && trackOpen) {
            trackOpen();
        }
        setIsOpen(!isOpen);
    };

    const openMoreInfoModal = (data: string): void => {
        const footer = showAccessoryDisclaimer ? <BnbLabel label="accessoryDisclaimer" useHTML /> : undefined;
        dispatch(
            showModal({
                type: MODAL_CONTENT,
                content: data,
                useHTML: true,
                title: getBnbLabel({ commonSettings, bnbSettings }, "moreInfo"),
                footer,
            }),
        );
    };

    return (
        <Styles.Wrapper isOpen={isOpen}>
            {!noDropdown ? (
                <Styles.DropDownAction
                    as="a"
                    onClick={toggleState}
                    id={`${uniqueId}-header`}
                    aria-expanded={isOpen}
                    aria-controls={uniqueId}
                >
                    <Styles.DropDownTitle role="heading" aria-level={4}>
                        {title}
                    </Styles.DropDownTitle>
                    <IconWrapper.ToggleIconWrapper isDown={isOpen} aria-hidden="true">
                        <Icon variant="chevronDown" />
                    </IconWrapper.ToggleIconWrapper>
                </Styles.DropDownAction>
            ) : (
                <SrOnly id={`${uniqueId}-header`}>{title}</SrOnly>
            )}
            {(isOpen || noDropdown) && (
                <div key={uniqueId} id={uniqueId} aria-labelledby={`${uniqueId}-header`}>
                    {specs.map((spec) => (
                        <Styles.DropDown key={spec.id}>
                            {spec.title && (
                                <Type.Strong as="h5" m={0}>
                                    {!!spec.title && spec.title}
                                    {/* Disclaimer is only used for WLTP tooltips which currently is only used in summary */}
                                    {!!spec.disclaimer && <Popover showOnClick content={spec.disclaimer} />}
                                </Type.Strong>
                            )}
                            {children}
                            {!!spec.data &&
                                spec.data.map((data) => (
                                    <SummaryStyles.Row key={data.id}>
                                        <Flex.Row justifyContent="space-between">
                                            <Flex.Col width={singleColumn ? "100%" : undefined}>
                                                {data.name}
                                                {data.description && (
                                                    <Button.Link onClick={() => openMoreInfoModal(data.description!)}>
                                                        <IconWrapper.IconWrapper>
                                                            <Icon variant="info" />
                                                        </IconWrapper.IconWrapper>

                                                        <SrOnly>
                                                            {data.name}
                                                            <BnbLabel label="bnbA11yButtonMoreInfo" />
                                                        </SrOnly>
                                                    </Button.Link>
                                                )}
                                                {!!getEvDisclaimer(bnbSettings, commonSettings, data.internalCode) && (
                                                    <>
                                                        {" "}
                                                        <Popover
                                                            showOnClick
                                                            content={
                                                                getEvDisclaimer(
                                                                    bnbSettings,
                                                                    commonSettings,
                                                                    data.internalCode,
                                                                )!
                                                            }
                                                            useHTML
                                                        />
                                                    </>
                                                )}
                                            </Flex.Col>
                                            {!singleColumn && (
                                                <Flex.Col>
                                                    {commonSettings.culture ? (
                                                        <SpecificationValue currentSpec={data} allSpecs={spec.data!} />
                                                    ) : (
                                                        `${data.value} ${data.unit}`.trim()
                                                    )}
                                                    {data.tooltip && (
                                                        <>
                                                            {" "}
                                                            <Popover showOnClick content={data.tooltip} useHTML />
                                                        </>
                                                    )}
                                                </Flex.Col>
                                            )}
                                        </Flex.Row>
                                    </SummaryStyles.Row>
                                ))}
                        </Styles.DropDown>
                    ))}
                </div>
            )}
        </Styles.Wrapper>
    );
};

export default SpecList;
